import React, { useEffect } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { isArrayLength } from '../../util/genericHelpers';

import {
  Page,
  LayoutSingleColumn,
  ListingCard,
  NamedLink,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SectionHero from '../SeoCategoryPage/SectionHero/SectionHero';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { getTypeHubBySlug, TYPE_HUBS } from '../../util/typeSeoContent';
import { fetchTypeListings, getTypeListings } from './SeoTypePage.duck';

import css from '../SeoCategoryPage/SeoCategoryPage.module.css';
import tcss from './SeoTypePage.module.css';

// ─── Quick Answer Box (mirrors SeoCategoryPage) ───────────────────────────────
const QuickAnswerBox = ({ text }) => {
  if (!text) return null;
  return (
    <div className={css.quickAnswerBox}>
      <div className={css.quickAnswerLabel}>Quick Answer</div>
      <p className={css.quickAnswerText}>{text}</p>
    </div>
  );
};

// ─── Breadcrumb ───────────────────────────────────────────────────────────────
const TypeBreadcrumb = ({ h1 }) => (
  <nav className={css.topBreadcrumb} aria-label="Breadcrumb">
    <NamedLink name="LandingPage" className={css.topCrumb}>
      Home
    </NamedLink>
    <span className={css.topCrumbSep}>/</span>
    <NamedLink name="SeoCategoriesPage" className={css.topCrumb}>
      Trailer Rentals
    </NamedLink>
    <span className={css.topCrumbSep}>/</span>
    <span className={css.topCrumbCurrent}>{h1}</span>
  </nav>
);

// ─── Editorial sub-sections (what size / what tows it / cost) ──────────────────
const TypeSubSections = ({ sections }) => {
  if (!isArrayLength(sections)) return null;
  return (
    <div className={css.typeSections}>
      {sections.map((s, i) => (
        <div key={i} className={css.typeSection}>
          <h3 className={css.typeSectionHeading}>{s.heading}</h3>
          <p className={css.typeSectionBody}>{s.body}</p>
        </div>
      ))}
    </div>
  );
};

// ─── FAQ (FAQPage schema rendered as <dl>) ─────────────────────────────────────
const FaqSection = ({ faqs }) => {
  if (!isArrayLength(faqs)) return null;
  return (
    <div className={css.faqSection}>
      <h2 className={css.faqHeading}>Frequently Asked Questions</h2>
      <dl className={css.faqList}>
        {faqs.map((faq, idx) => (
          <div key={idx} className={css.faqItem}>
            <dt className={css.faqQuestion}>{faq.question}</dt>
            <dd className={css.faqAnswer}>{faq.answer}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ─── Related links (guides + other type hubs) ─────────────────────────────────
const TypeRelatedLinks = ({ hub }) => {
  const otherHubs = TYPE_HUBS.filter(h => h.slug !== hub.slug);
  const hasBlogs = isArrayLength(hub.relatedBlogs);
  if (!otherHubs.length && !hasBlogs) return null;
  return (
    <div className={tcss.cityRelatedLinks}>
      <h2 className={css.faqHeading}>Related Trailer Rentals &amp; Guides</h2>
      <div className={tcss.relatedGrid}>
        {otherHubs.map(h => (
          <a key={h.slug} href={`/${h.slug}`} className={tcss.relatedLink}>
            {h.h1} &rarr;
          </a>
        ))}
        {hasBlogs
          ? hub.relatedBlogs.map(b => (
              <a key={b.slug} href={`/blog/${b.slug}`} className={tcss.relatedLink}>
                {b.label} &rarr;
              </a>
            ))
          : null}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SeoTypePageComponent = ({ scrollingDisabled, params, config, listings }) => {
  const dispatch = useDispatch();
  const slug = params?.seoTypeSlug || params?.slug || '';
  const hub = getTypeHubBySlug(slug);

  // Re-fetch on client-side navigation (when loadData didn't run for this slug).
  useEffect(() => {
    if (hub) dispatch(fetchTypeListings(slug, config));
  }, [slug]);

  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const siteUrl = config?.marketplaceRootURL || 'https://bigloads.com';

  // Unknown slug (route registered but no matching hub in typeSeoContent.js).
  // The route regex only allows the known hub slugs, so this is a safety net
  // only; render the real NotFoundPage so crawlers get a proper 404 (not a
  // 200 with an infinite spinner).
  if (!hub) {
    return <NotFoundPage />;
  }

  const pageUrl = `${siteUrl}/${hub.slug}`;

  // ── Real price range from live listings (never fabricated) ──────────────
  const priceRange = (() => {
    if (!isArrayLength(listings)) return null;
    const amounts = listings
      .map(l => l?.attributes?.price)
      .filter(p => p && typeof p.amount === 'number' && p.amount > 0)
      .map(p => p.amount);
    if (!amounts.length) return null;
    const min = Math.floor(Math.min(...amounts) / 100);
    const max = Math.floor(Math.max(...amounts) / 100);
    return min === max ? `$${min}/day` : `$${min}\u2013$${max}/day`;
  })();

  const listingCount = isArrayLength(listings) ? listings.length : 0;
  const quickAnswer = typeof hub.quickAnswer === 'function' ? hub.quickAnswer(priceRange) : hub.quickAnswer;
  const searchAllUrl = `/s?pub_trailerType=${hub.trailerType}`;

  // ── Structured data ─────────────────────────────────────────────────────
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      // /states is the real "Trailer Rentals by State" index route (SeoCategoriesPage).
      { '@type': 'ListItem', position: 2, name: 'Trailer Rentals', item: `${siteUrl}/states` },
      { '@type': 'ListItem', position: 3, name: hub.h1, item: pageUrl },
    ],
  };

  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: hub.seoTitle,
    description: hub.seoDescription,
    image: [`${siteUrl}${hub.heroImage}`],
  };

  const serviceSchema = {
    '@type': 'Service',
    '@id': `${pageUrl}#service`,
    name: hub.h1,
    serviceType: 'Trailer rental',
    provider: { '@type': 'Organization', name: marketplaceName, url: siteUrl },
    areaServed: { '@type': 'Country', name: 'United States' },
    url: pageUrl,
  };

  const faqSchema = isArrayLength(hub.faqs)
    ? {
        '@type': 'FAQPage',
        mainEntity: hub.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }
    : null;

  const schemas = [webPageSchema, serviceSchema, breadcrumbSchema, faqSchema].filter(Boolean);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={hub.seoDescription}
      title={hub.seoTitle}
      canonicalUrl={pageUrl}
      facebookImages={[{ url: `${siteUrl}${hub.heroImage}`, width: 1200, height: 630 }]}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <SectionHero bannerTitle={hub.heroHeading || hub.h1} bannerImage={hub.heroImage} />

        <div className={css.pageRoot}>
          <TypeBreadcrumb h1={hub.h1} />

          <QuickAnswerBox text={quickAnswer} />

          <div className={css.taxDetailsSec}>
            <div className={css.taxDetailsLeft}>
              {/* Live national listings for this trailer type */}
              <h2 className={css.typeSectionsHeading}>{`Available ${hub.nounPlural} to Rent`}</h2>

              <div className={css.mapListingsCards}>
                {isArrayLength(listings)
                  ? listings.map(l => (
                      <ListingCard
                        className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={[
                          '(max-width: 549px) 100vw',
                          '(max-width: 767px) 50vw',
                          '(max-width: 1439px) 26vw',
                          '(max-width: 1920px) 18vw',
                          '14vw',
                        ].join(', ')}
                        setActiveListing={() => {}}
                      />
                    ))
                  : null}
              </div>

              {listingCount > 0 ? (
                <a className={tcss.viewAllLink} href={searchAllUrl}>
                  Browse all {hub.nounPlural.toLowerCase()} &rarr;
                </a>
              ) : (
                <div className={css.emptyListings}>
                  <h2 className={css.emptyListingsHeading}>New trailers added all the time</h2>
                  <p className={css.emptyListingsText}>
                    BigLoads is growing across the country. Browse all trailers, or list your own and
                    reach renters looking for exactly this.
                  </p>
                  <div className={css.emptyListingsCtas}>
                    <NamedLink name="NewListingPage" className={css.emptyListingsPrimary}>
                      List your trailer
                    </NamedLink>
                    <NamedLink name="SearchPage" className={css.emptyListingsSecondary}>
                      Browse all trailers
                    </NamedLink>
                  </div>
                </div>
              )}

              {/* Editorial body (the ranking content) */}
              <div className={css.pageHeader}>
                {isArrayLength(hub.intro)
                  ? hub.intro.map((sec, i) => (
                      <div key={i}>
                        <h2>{sec.heading}</h2>
                        <p>{sec.body}</p>
                      </div>
                    ))
                  : null}
              </div>

              <TypeSubSections sections={hub.subSections} />

              <FaqSection faqs={hub.faqs} />

              <TypeRelatedLinks hub={hub} />

              {/* Owner-acquisition CTA band */}
              <div className={tcss.ownerCtaBand}>
                <div>
                  <h2>Own a {hub.noun.toLowerCase()}?</h2>
                  <p>List it on BigLoads and earn money renting it out when you're not using it.</p>
                </div>
                <NamedLink name="NewListingPage" className={css.emptyListingsPrimary}>
                  List Your Trailer
                </NamedLink>
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoTypePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const EnhancedSeoTypePage = props => {
  const config = useConfiguration();
  const params = useParams();
  return <SeoTypePageComponent params={params} config={config} {...props} />;
};

const mapStateToProps = state => ({
  listings: getTypeListings(state),
  scrollingDisabled: isScrollingDisabled(state),
});

const SeoTypePage = compose(connect(mapStateToProps))(EnhancedSeoTypePage);

export default SeoTypePage;
